import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Lock, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { IntakeReviewPageV6 } from "./IntakeReviewPageV6";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REVIEW_STATUSES = new Set(["new", "reviewing", "awaiting_isr_review", "isr_reviewing"]);

export const IntakeReviewPageV7 = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin, isManager, isSalesRep } = useAuth();
  const revisionRef = useRef(1);
  const [status, setStatus] = useState(null);
  const [validationError, setValidationError] = useState("");
  const [converting, setConverting] = useState(false);
  const fromKanban = searchParams.get("from") === "kanban";

  useEffect(() => {
    axios.get(`${API}/intake-submissions/${id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    }).then((response) => {
      setStatus(response.data.status || "");
      revisionRef.current = Number(response.data.submission_revision || 1);
    }).catch((error) => {
      setStatus("");
      setValidationError(error.response?.data?.detail || "Failed to load customer design request");
    });
  }, [id]);

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use((config) => {
      const url = String(config.url || "");
      const isReviewWrite = url.includes(`/intake-submissions/${id}/v4`)
        || url.includes(`/intake-submissions/${id}/approve-v4`);

      if (isReviewWrite) {
        config.data = {
          ...(config.data || {}),
          expected_revision: revisionRef.current,
        };
      }

      return config;
    });

    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        const url = String(response.config?.url || "");
        const isReviewWrite = url.includes(`/intake-submissions/${id}/v4`)
          || url.includes(`/intake-submissions/${id}/approve-v4`);
        if (isReviewWrite) {
          revisionRef.current = Number(response.data?.submission_revision || revisionRef.current);
          setValidationError("");
          if (response.data?.status) setStatus(response.data.status);
        }
        return response;
      },
      (error) => {
        const url = String(error.config?.url || "");
        const isReviewWrite = url.includes(`/intake-submissions/${id}/v4`)
          || url.includes(`/intake-submissions/${id}/approve-v4`);
        if (isReviewWrite) {
          setValidationError(error.response?.data?.detail || "The request could not be saved. Review the form and try again.");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [id]);

  const approveAndCreate = async () => {
    if (!window.confirm("Approve this request and create one Graphics project per SKU?")) return;
    setConverting(true);
    try {
      const response = await axios.post(
        `${API}/intake-submissions/${id}/convert-v4`,
        { split_by_sku: true },
      );
      const count = response.data.created_count || response.data.group_count || 0;
      toast.success(`Approved and created ${count} project${count === 1 ? "" : "s"}`);
      navigate("/kanban");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve and create projects");
    } finally {
      setConverting(false);
    }
  };

  const locked = status !== null && !isAdmin && !isManager && (!isSalesRep || !REVIEW_STATUSES.has(status));
  const maySubmit = isAdmin || isManager || isSalesRep;
  const returnPath = fromKanban ? "/kanban" : "/intake-submissions";

  if (status === null) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">Loading customer design request...</div>;
  }

  return (
    <div>
      {validationError && (
        <div className="sticky top-4 z-[60] mx-auto mb-4 flex max-w-5xl items-start gap-3 rounded-xl border border-red-700 bg-red-950/95 px-5 py-4 text-red-100 shadow-2xl" role="alert">
          <AlertTriangle size={20} className="mt-0.5 shrink-0" />
          <div className="flex-1">
            <strong className="block">This request needs attention before it can be submitted</strong>
            <p className="mt-1 text-sm">{validationError}</p>
          </div>
          <button type="button" onClick={() => setValidationError("")} aria-label="Dismiss validation message" className="rounded p-1 hover:bg-red-900">
            <X size={16} />
          </button>
        </div>
      )}

      {locked && (
        <div className="fixed left-1/2 top-20 z-50 -translate-x-1/2 flex items-center gap-2 rounded-lg border border-amber-700/60 bg-amber-950 px-4 py-3 text-sm text-amber-100 shadow-xl">
          <Lock size={16} />
          Read-only for Sales Ops after submission to Graphics. Sales Ops Management and Graphics Admin may still correct the request.
        </div>
      )}

      {fromKanban && isAdmin && status === "submitted_to_graphics" && (
        <div className="fixed bottom-6 right-6 z-[80] rounded-xl border border-emerald-700 bg-card p-3 shadow-2xl">
          <button
            type="button"
            onClick={approveAndCreate}
            disabled={converting}
            className="rounded-lg bg-emerald-700 px-5 py-3 text-sm font-semibold text-white disabled:opacity-50"
          >
            {converting ? "Creating Projects..." : "Approve & Create Projects"}
          </button>
        </div>
      )}

      <IntakeReviewPageV6
        locked={locked}
        maySubmit={maySubmit}
        returnPath={returnPath}
      />
    </div>
  );
};
